import { useState, useCallback, useRef } from 'react'
import { CellValueChangedEvent } from '@ag-grid-community/core'
import { CensusFileRowGridData } from '../types/census-file-row-grid-data'

interface DirtyFields {
  [fieldName: string]: boolean
}

type GridRow = CensusFileRowGridData & { dirtyFields?: DirtyFields }

interface EditHistoryEntry {
  rowId: number
  fieldName: string
  // The value the cell held immediately before this edit, so undo can step back
  // one edit at a time instead of jumping straight to the census file value.
  previousValue: any
}

export interface MarkRowDirtyResult {
  // 'appended'  - a new edit was recorded
  // 'reverted'  - the field is back where it started, so its edits were dropped
  // 'duplicate' - a redundant grid event that recorded nothing
  status: 'appended' | 'reverted' | 'duplicate'
  // How many entries this call removed from the edit history. Callers mirroring
  // the history in their own undo stack must drop the same number - dropping a
  // fixed one leaves stale entries that make Undo look broken.
  removedEdits: number
}

const fieldKey = (rowId: number, fieldName: string) => `${rowId}:${fieldName}`

// Tracks cell edits on the CMv2 error bucket grid, where rows are corrected
// through select editors (Gender, ContributionGroup, ...), and gives the page an
// undo stack it can keep in step with its own ignore-toggle stack.
//
// The edit history is the single source of truth for whether a field is dirty: a
// field is dirty while it has at least one entry, and clean once its entries are
// gone. Comparing the cell against the census file value can't be used for that,
// because a column's valueGetter may normalize what the grid shows (see
// findMatchingOption in configure-edit-field) - a row whose file value is
// 'female' displays and edits as 'Female', so an equality check would report the
// field as still dirty after it had been fully undone.
//
// The sibling useMarkRowDirty is left alone for the grids that don't have those
// select editors.
export const useMarkRowDirtyErrors = () => {
  // Refs hold the authoritative state so a single grid event can read and update
  // it synchronously; the useState copies exist to re-render consumers.
  const dirtyRowsRef = useRef<GridRow[]>([])
  const historyRef = useRef<EditHistoryEntry[]>([])
  const [dirtyRows, setDirtyRows] = useState<GridRow[]>([])
  const [editHistory, setEditHistory] = useState<EditHistoryEntry[]>([])

  // The value each field held before its first edit. A field is back to its
  // starting point when it returns to this value, compared against what the grid
  // reported rather than the raw file value so normalized columns behave like
  // plain ones.
  const baselineRef = useRef<Record<string, any>>({})
  // AG Grid's select cell editor can fire cellValueChanged twice for a single
  // user selection (once from the option-selected handler, once from its own
  // Enter-key handling) - remember the value last recorded per field so the
  // redundant duplicate doesn't get logged as a second edit.
  const lastRecordedValueRef = useRef<Record<string, any>>({})

  const publish = useCallback(() => {
    setDirtyRows([...dirtyRowsRef.current])
    setEditHistory([...historyRef.current])
  }, [])

  const forgetField = useCallback((key: string) => {
    delete baselineRef.current[key]
    delete lastRecordedValueRef.current[key]
  }, [])

  const markRowDirty = useCallback(
    (params: CellValueChangedEvent<GridRow>): MarkRowDirtyResult => {
      const { newValue, oldValue, data, colDef } = params
      const fieldName = colDef.field as string
      const key = fieldKey(data.id, fieldName)

      // Nothing actually changed: either the grid re-committed the same value,
      // or this exact value is already recorded for the field.
      const alreadyRecorded =
        key in lastRecordedValueRef.current &&
        lastRecordedValueRef.current[key] === newValue
      if (newValue === oldValue || alreadyRecorded) {
        return { status: 'duplicate', removedEdits: 0 }
      }
      lastRecordedValueRef.current[key] = newValue

      if (!(key in baselineRef.current)) {
        baselineRef.current[key] = oldValue
      }

      if (newValue === baselineRef.current[key]) {
        // Back to where this field started, so it has no outstanding edits.
        const before = historyRef.current.length
        historyRef.current = historyRef.current.filter(
          (edit) => !(edit.rowId === data.id && edit.fieldName === fieldName)
        )
        const removedEdits = before - historyRef.current.length

        forgetField(key)
        if (data.dirtyFields) {
          delete data.dirtyFields[fieldName]
        }
        if (Object.keys(data.dirtyFields || {}).length === 0) {
          data.isDirty = false
          dirtyRowsRef.current = dirtyRowsRef.current.filter(
            (row) => row.id !== data.id
          )
        }

        publish()
        return { status: 'reverted', removedEdits }
      }

      data.isDirty = true
      data.dirtyFields = { ...(data.dirtyFields || {}), [fieldName]: true }
      if (data.rowData && data.rowData[fieldName] === undefined) {
        // Keep the JSON key in the payload when the file had no value for it
        data.rowData[fieldName] = ''
      }

      if (!dirtyRowsRef.current.some((row) => row.id === data.id)) {
        dirtyRowsRef.current = [...dirtyRowsRef.current, data]
      }
      historyRef.current = [
        ...historyRef.current,
        { rowId: data.id, fieldName, previousValue: oldValue },
      ]

      publish()
      return { status: 'appended', removedEdits: 0 }
    },
    [forgetField, publish]
  )

  const undoLastEdit = useCallback(() => {
    if (historyRef.current.length === 0) return // Nothing to undo

    const lastEdit = historyRef.current[historyRef.current.length - 1]
    historyRef.current = historyRef.current.slice(0, -1)

    const { rowId, fieldName } = lastEdit
    const key = fieldKey(rowId, fieldName)
    const stillEdited = historyRef.current.some(
      (edit) => edit.rowId === rowId && edit.fieldName === fieldName
    )
    const row = dirtyRowsRef.current.find((candidate) => candidate.id === rowId)

    if (row) {
      if (stillEdited) {
        // Step back to the value from before the undone edit; the field keeps
        // its earlier edits, so it stays dirty.
        row[fieldName] = lastEdit.previousValue
        delete lastRecordedValueRef.current[key]
      } else {
        // Every edit for this field is undone. Restore the census file value so
        // the cell matches the original exactly even on normalized columns.
        row[fieldName] =
          row.rowData && fieldName in row.rowData
            ? row.rowData[fieldName]
            : lastEdit.previousValue
        forgetField(key)

        if (row.dirtyFields) {
          delete row.dirtyFields[fieldName]
        }
        if (Object.keys(row.dirtyFields || {}).length === 0) {
          row.isDirty = false
          dirtyRowsRef.current = dirtyRowsRef.current.filter(
            (candidate) => candidate.id !== rowId
          )
        }
      }
    }

    publish()
  }, [forgetField, publish])

  const cleanAllRows = useCallback(() => {
    dirtyRowsRef.current.forEach((row) => {
      row.isDirty = false
      if (row.dirtyFields) {
        row.dirtyFields = {}
      }
    })
    dirtyRowsRef.current = []
    historyRef.current = []
    baselineRef.current = {}
    lastRecordedValueRef.current = {}
    publish()
  }, [publish])

  const editCount = editHistory.length

  return {
    editCount,
    cleanAllRows,
    markRowDirty,
    undoLastEdit,
    dirtyRows,
    editHistory,
  }
}
